import clsx from 'clsx';
import { useReducedMotion } from 'motion/react';
import * as React from 'react';

import { usePersistentValue, usePrevious } from '@gitbook/util-react-hooks';

import { SidePanelExpandableContext } from './context';

export interface PageWithSidePanelProps {
    /** Main content always displayed. */
    children: React.ReactNode;
    /** Side-content to render, null if it should be closed. */
    sidePanel: React.ReactNode | null;
}

/**
 * Panel transition to be used in motion/react, duration is in seconds.
 */
export function usePanelTransition() {
    const shouldReduceMotion = useReducedMotion();
    return {
        duration: shouldReduceMotion ? 0 : 0.18,
        ease: [0.4, 0, 0.2, 1],
    };
}

/**
 * Panel transition to be used in CSS.
 */
export function usePanelTransitionStyle() {
    const transition = usePanelTransition();
    return {
        transitionDuration: `${transition.duration}s`,
        transitionTimingFunction: `cubic-bezier(${transition.ease.join(',')})`,
    };
}

/**
 * Component to render a main body with a side-panel on the right.
 */
export function PageWithSidePanel(props: PageWithSidePanelProps): React.ReactElement {
    const { children, sidePanel } = props;

    const [isExpanded, setExpanded] = React.useState(false);
    const expandableContext = React.useMemo(() => ({ isExpanded, setExpanded }), [isExpanded]);
    const transitionStyle = usePanelTransitionStyle();
    const persistentSidePanel = usePersistentValue(sidePanel);
    const isOpened = !!sidePanel;
    const { isAnimating, animatedProps } = useIsAnimating({ isOpened });

    return (
        <SidePanelExpandableContext.Provider value={expandableContext}>
            <div
                className={clsx(
                    'flex min-h-0 flex-1 overflow-y-auto overflow-x-hidden bg-side',
                    '[--panel-padding:0.375rem]',
                    isExpanded
                        ? '[--panel-width:34rem]'
                        : '[--panel-width:22rem] max-sm:[--panel-width:100vw]',
                    '[--max-panel-width:min(var(--panel-width),calc(100vw-var(--app-layout-spacing-unit)*2))]',
                    isOpened ? 'sm:pr-[calc(var(--panel-width)+var(--panel-padding))]' : null
                )}
                style={{ ...transitionStyle, transitionProperty: 'padding' }}
            >
                <div
                    className="app-framing-styles relative flex flex-1 flex-col"
                    // Allow the parent AppLayout to detect whether a child screen is rendering with sidepanels, to skip framing styles. See app.css for more.
                    data-side-panel-frame
                >
                    {children}
                </div>
                <div
                    className={clsx(
                        'absolute inset-y-1.5 right-1.5 z-sidebar flex flex-col overflow-hidden rounded-xl border-0.5 border-control bg-base shadow-subtle',
                        'max-sm:top-[--app-top-unavailable]',
                        'w-screen max-w-[--max-panel-width]',
                        isOpened
                            ? null
                            : clsx(
                                  'pointer-events-none translate-x-[calc(var(--max-panel-width)+var(--panel-padding))]',
                                  isAnimating ? null : 'invisible'
                              )
                    )}
                    style={{ ...transitionStyle, transitionProperty: 'transform' }}
                    {...animatedProps}
                >
                    {isAnimating || isOpened ? persistentSidePanel : null}
                </div>
            </div>
        </SidePanelExpandableContext.Provider>
    );
}

/**
 * Check if the element is animating / transitioning.
 */
function useIsAnimating(props: { isOpened: boolean }) {
    const { isOpened } = props;
    const shouldReduceMotion = useReducedMotion();
    const [isAnimating, setIsAnimating] = React.useState(false);
    const previousIsOpened = usePrevious(isOpened);
    React.useLayoutEffect(() => {
        if (
            previousIsOpened !== undefined &&
            previousIsOpened !== isOpened &&
            !shouldReduceMotion
        ) {
            setIsAnimating(true);
        }
    }, [previousIsOpened, isOpened, shouldReduceMotion]);
    return {
        isAnimating,
        animatedProps: {
            onTransitionEnd: (event: React.TransitionEvent) => {
                if (event.target === event.currentTarget) {
                    setIsAnimating(false);
                }
            },
        },
    };
}
